import { jsx } from 'react/jsx-runtime';
import { isEmptyArray } from '@flex-design-system/fx-utils/common';
import { useTimeout } from '@flex-design-system/fx-utils/hooks';
import { MagnifyingglassIcon } from '@flex-design-system/legacy-flex-icons';
import { identity } from '@fxts/core';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import classNames from 'classnames';
import { forwardRef, useRef, useState, useCallback } from 'react';
import useListNavigationSelect from '../../hooks/useListNavigationSelect.mjs';
import { findMatchedItemIndex } from '../../utils/element.mjs';
import { stopEvent } from '../../utils/event.mjs';
import { isEscKey } from '../../utils/keyboard.mjs';
import TextField from '../text-field/TextField.mjs';
import { FOCUSED_SELECT_OPTION_CLASSNAME } from './constants/styles.mjs';
import { searchInputStyle } from './select.vanilla.mjs';
import { isMatchedItem } from './utils/item.mjs';

const BaseSearchInput = forwardRef(
  ({
    className,
    onKeyDown,
    onItemSelect,
    scope,
    getItems,
    leftSlot,
    searchFormatter = identity,
    keepPreviousActiveItemFocus = false,
    loop = false,
    ...props
  }, forwardedRef) => {
    const previousActiveIndexRef = useRef(void 0);
    const { setTimeout } = useTimeout({
      clearOnNewTimeout: true
    });
    const ref = useRef(null);
    const composedRefs = useComposedRefs(ref, forwardedRef);
    const [displayValue, setDisplayValue] = useState("");
    const [, { setActiveIndex, resetActiveIndex, ...listElementProps }] = useListNavigationSelect({
      onItemSelect,
      focusClassName: FOCUSED_SELECT_OPTION_CLASSNAME,
      loop,
      getListRef: getItems,
      onKeyDown: (e) => {
        if (!isEscKey(e.key)) {
          stopEvent(e);
        }
      }
    });
    const handleChange = useCallback(
      (e) => {
        const searchValue = searchFormatter(e.target.value);
        const matchedItemIndexCandidates = findMatchedItemIndex({
          getListRef: getItems,
          filter: ({ value, textValue }) => {
            if (searchValue === "") {
              return true;
            }
            return isMatchedItem(
              {
                textValue,
                value: value.toString()
              },
              searchValue
            );
          }
        });
        if (!isEmptyArray(matchedItemIndexCandidates)) {
          previousActiveIndexRef.current = matchedItemIndexCandidates[0];
          if (matchedItemIndexCandidates[0] === void 0) {
            console.warn(
              "[packages/linear] matchedItemIndexCandidates[0] === undefined \uC778 \uC0C1\uD669\uC774 \uC758\uB3C4\uC778\uC9C0 \uD655\uC778\uD574 \uC8FC\uC138\uC694."
            );
          }
          setActiveIndex(matchedItemIndexCandidates[0]);
          return;
        }
        if (keepPreviousActiveItemFocus) {
          setTimeout(() => {
            if (previousActiveIndexRef.current !== void 0) {
              setActiveIndex(previousActiveIndexRef.current);
            }
          }, 0);
        }
      },
      [
        getItems,
        keepPreviousActiveItemFocus,
        searchFormatter,
        setActiveIndex,
        setTimeout
      ]
    );
    return /* @__PURE__ */ jsx(
      TextField,
      {
        className: classNames(searchInputStyle, className),
        leftSlot: leftSlot === void 0 ? /* @__PURE__ */ jsx(MagnifyingglassIcon, { size: 18, color: "grayLight" }) : leftSlot,
        ...props,
        ...listElementProps,
        value: displayValue,
        onChange: composeEventHandlers(props.onChange, (e) => {
          setDisplayValue(e.target.value);
          handleChange(e);
        }),
        size: "small",
        role: "combobox",
        variant: "solid",
        ref: composedRefs
      }
    );
  }
);
const BaseStaticInput = forwardRef(
  ({
    className,
    onKeyDown,
    onItemSelect,
    scope,
    getItems,
    leftSlot,
    rightSlot,
    loop = false,
    ...props
  }, forwardedRef) => {
    const [, { setActiveIndex, resetActiveIndex, ...listElementProps }] = useListNavigationSelect({
      onItemSelect,
      focusClassName: FOCUSED_SELECT_OPTION_CLASSNAME,
      loop,
      getListRef: getItems,
      onKeyDown: (e) => {
        if (!isEscKey(e.key)) {
          stopEvent(e);
        }
      }
    });
    return /* @__PURE__ */ jsx(
      TextField,
      {
        className: classNames(searchInputStyle, className),
        leftSlot: leftSlot ?? /* @__PURE__ */ jsx(MagnifyingglassIcon, { color: "grayLight" }),
        rightSlot,
        ...props,
        ...listElementProps,
        onChange: props.onChange,
        size: "small",
        role: "combobox",
        variant: "solid",
        ref: forwardedRef
      }
    );
  }
);

export { BaseSearchInput, BaseStaticInput };
//# sourceMappingURL=SearchInput.mjs.map
